import { useState, useEffect } from "react";
import { X, FirstAid, ChartLineUp } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";
import { API } from "@/App";
import { PlayerPhoto } from "@/components/PlayerPhoto";

function PosBadge({ pos }) {
  const cls = `pos-bg-${(pos || "").toLowerCase()} pos-${(pos || "").toLowerCase()}`;
  return (
    <span className={`inline-flex items-center justify-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm ${cls}`}>
      {pos}
    </span>
  );
}

function StatBlock({ label, value, sub }) {
  return (
    <div className="text-center">
      <p className="font-mono font-bold text-lg text-white">{value || '—'}</p>
      <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-[#666]">{label}</p>
      {sub && <p className="text-[9px] font-mono text-[#444]">{sub}</p>}
    </div>
  );
}

export default function PlayerDetailModal({ playerId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    document.body.classList.add('modal-open');
    axios.get(`${API}/player/${playerId}/detail`)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    return () => { document.body.classList.remove('modal-open'); };
  }, [playerId]);

  if (!playerId) return null;

  const posColors = { QB: '#ff6b6b', RB: '#4ecdc4', WR: '#45b7d1', TE: '#f9ca24' };
  const barColor = posColors[data?.position] || '#ccff00';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 overscroll-contain" data-testid="player-detail-modal" onTouchMove={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-lg max-h-[85vh] bg-[#171717] border border-[#262626] flex flex-col" data-testid="player-detail-content">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#262626] shrink-0">
          {loading ? (
            <div className="h-10 w-48 skeleton rounded-sm" />
          ) : data ? (
            <div className="flex items-center gap-3">
              <PlayerPhoto playerId={playerId} name={data.name} position={data.position} size={44} />
              <div>
                <h2 className="font-heading text-2xl text-white uppercase tracking-tight">{data.name}</h2>
                <p className="text-xs font-mono text-[#a3a3a3]">
                  {data.team || 'FA'} / #{data.number || '?'} / {data.age ? `${data.age}y` : ''} / {data.college || ''}
                </p>
              </div>
            </div>
          ) : (
            <p className="text-[#a3a3a3]">Player not found</p>
          )}
          <button onClick={onClose} className="p-2 text-[#666] hover:text-white" data-testid="close-player-detail">
            <X size={18} weight="bold" />
          </button>
        </div>

        {loading ? (
          <div className="p-5 space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-16 skeleton rounded-sm" />)}
          </div>
        ) : data ? (
          <div className="flex-1 overflow-y-auto">
            <div className="p-5 space-y-5">
              {/* Injury Status */}
              {data.injury_status && (
                <div className="flex items-center gap-3 p-3 border border-[#ff3b30]/30 bg-[#ff3b30]/5" data-testid="injury-status">
                  <FirstAid size={18} weight="bold" className="text-[#ff3b30] shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-[#ff3b30] uppercase">{data.injury_status}</p>
                    {data.injury_body_part && <p className="text-[10px] text-[#a3a3a3]">{data.injury_body_part}</p>}
                    {data.injury_notes && <p className="text-[10px] text-[#666]">{data.injury_notes}</p>}
                  </div>
                </div>
              )}

              {/* Bio Row */}
              <div className="flex items-center gap-4 flex-wrap text-xs font-mono text-[#a3a3a3]">
                {data.height && <span>{Math.floor(data.height / 12)}'{data.height % 12}" </span>}
                {data.weight && <span>{data.weight} lbs</span>}
                {data.years_exp != null && <span>{data.years_exp} yr exp</span>}
                {data.depth_chart_position && (
                  <span>Depth: {data.depth_chart_position} #{data.depth_chart_order || '?'}</span>
                )}
                <span className={`uppercase font-bold ${data.status === 'Active' ? 'text-[#4ade80]' : 'text-[#ff3b30]'}`}>
                  {data.status}
                </span>
              </div>

              {/* Season Stats */}
              {data.season_stats && (
                <div data-testid="season-stats">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666] mb-3">
                    {data.season} SEASON STATS ({data.season_stats.gp || 0} GP)
                  </p>
                  {data.position === "QB" ? (
                    <div className="grid grid-cols-4 gap-3">
                      <StatBlock label="PASS YD" value={Math.round(data.season_stats.pass_yd || 0).toLocaleString()} sub={`${data.season_stats.pass_cmp || 0}/${data.season_stats.pass_att || 0}`} />
                      <StatBlock label="PASS TD" value={data.season_stats.pass_td || 0} sub={`${data.season_stats.pass_int || 0} INT`} />
                      <StatBlock label="RUSH YD" value={Math.round(data.season_stats.rush_yd || 0)} sub={`${data.season_stats.rush_td || 0} TD`} />
                      <StatBlock label="PPR PTS" value={Math.round(data.season_stats.pts_ppr || 0)} />
                    </div>
                  ) : data.position === "RB" ? (
                    <div className="grid grid-cols-4 gap-3">
                      <StatBlock label="RUSH YD" value={Math.round(data.season_stats.rush_yd || 0).toLocaleString()} sub={`${data.season_stats.rush_att || 0} att`} />
                      <StatBlock label="RUSH TD" value={data.season_stats.rush_td || 0} />
                      <StatBlock label="REC" value={data.season_stats.rec || 0} sub={`${Math.round(data.season_stats.rec_yd || 0)} yd`} />
                      <StatBlock label="PPR PTS" value={Math.round(data.season_stats.pts_ppr || 0)} />
                    </div>
                  ) : data.position === "WR" || data.position === "TE" ? (
                    <div className="grid grid-cols-4 gap-3">
                      <StatBlock label="REC" value={data.season_stats.rec || 0} sub={`${data.season_stats.rec_tgt || 0} tgt`} />
                      <StatBlock label="REC YD" value={Math.round(data.season_stats.rec_yd || 0).toLocaleString()} />
                      <StatBlock label="REC TD" value={data.season_stats.rec_td || 0} />
                      <StatBlock label="PPR PTS" value={Math.round(data.season_stats.pts_ppr || 0)} />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-3">
                      <StatBlock label="PPR PTS" value={Math.round(data.season_stats.pts_ppr || 0)} />
                      <StatBlock label="GP" value={data.season_stats.gp || 0} />
                    </div>
                  )}
                </div>
              )}

              {/* Game Logs Chart */}
              {data.game_logs?.length > 0 && (
                <div data-testid="game-logs">
                  <div className="flex items-center gap-2 mb-3">
                    <ChartLineUp size={14} weight="bold" className="text-[#ccff00]" />
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#666]">
                      RECENT GAME LOG (PPR POINTS)
                    </p>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={[...data.game_logs].reverse()} margin={{ left: 0, right: 0, top: 5, bottom: 0 }}>
                      <XAxis dataKey="week" tick={{ fill: '#666', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(w) => `W${w}`} />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{ background: '#171717', border: '1px solid #262626', borderRadius: 0, fontFamily: 'JetBrains Mono', fontSize: 11, color: '#fff' }}
                        itemStyle={{ color: '#ccff00' }}
                        labelFormatter={(w) => `Week ${w}`}
                        formatter={(v) => [v.toFixed(1), 'PPR Pts']}
                      />
                      <Bar dataKey="pts_ppr" fill={barColor} radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Game Log Table */}
                  <div className="mt-3 border border-[#262626]">
                    <div className="grid grid-cols-6 gap-px bg-[#0a0a0a] text-[9px] font-semibold uppercase tracking-wider text-[#666] px-3 py-1.5 border-b border-[#262626]">
                      <span>WK</span>
                      <span className="text-right">PTS</span>
                      {data.position === "QB" ? (
                        <>
                          <span className="text-right">PASS</span>
                          <span className="text-right">P.TD</span>
                          <span className="text-right">RUSH</span>
                          <span className="text-right">R.TD</span>
                        </>
                      ) : (
                        <>
                          <span className="text-right">{data.position === "RB" ? "RUSH" : "REC"}</span>
                          <span className="text-right">{data.position === "RB" ? "R.YD" : "R.YD"}</span>
                          <span className="text-right">{data.position === "RB" ? "REC" : "TGT"}</span>
                          <span className="text-right">TD</span>
                        </>
                      )}
                    </div>
                    {[...data.game_logs].reverse().map((gl) => (
                      <div key={gl.week} className="grid grid-cols-6 gap-px px-3 py-1.5 border-b border-[#1a1a1a] text-xs font-mono">
                        <span className="text-[#a3a3a3]">{gl.week}</span>
                        <span className="text-right text-[#ccff00] font-bold">{gl.pts_ppr?.toFixed(1)}</span>
                        {data.position === "QB" ? (
                          <>
                            <span className="text-right text-white">{Math.round(gl.pass_yd || 0)}</span>
                            <span className="text-right text-white">{gl.pass_td || 0}</span>
                            <span className="text-right text-white">{Math.round(gl.rush_yd || 0)}</span>
                            <span className="text-right text-white">{gl.rush_td || 0}</span>
                          </>
                        ) : (
                          <>
                            <span className="text-right text-white">{data.position === "RB" ? Math.round(gl.rush_yd || 0) : (gl.rec || 0)}</span>
                            <span className="text-right text-white">{data.position === "RB" ? Math.round(gl.rush_yd || 0) : Math.round(gl.rec_yd || 0)}</span>
                            <span className="text-right text-white">{data.position === "RB" ? (gl.rec || 0) : (gl.rec_tgt || 0)}</span>
                            <span className="text-right text-white">{(gl.rush_td || 0) + (gl.rec_td || 0)}</span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!data.game_logs?.length && !data.season_stats && (
                <div className="text-center py-6 text-xs text-[#666]">No stats available for this player</div>
              )}
            </div>
          </div>
        ) : (
          <div className="p-8 text-center text-sm text-[#666]">Failed to load player details</div>
        )}
      </div>
    </div>
  );
}
